import { mediaList, featuredIds } from '@/mocks/media';
import { newsList } from '@/mocks/news';
import type { MediaItem, MediaType, NewsItem } from '@/types/media';

const allMedia = mediaList as unknown as MediaItem[];
const allNews = newsList as unknown as NewsItem[];

export function getAllMedia(): MediaItem[] {
  return allMedia;
}

export function getMediaById(id: string): MediaItem | undefined {
  return allMedia.find((item) => item.id === id);
}

export function getFeaturedMedia(): MediaItem[] {
  const list = featuredIds
    .map((id) => allMedia.find((item) => item.id === id))
    .filter((item): item is MediaItem => Boolean(item));
  return list.length ? list : [...allMedia].sort((a, b) => b.popularity - a.popularity).slice(0, 5);
}

export function getByType(type: string): MediaItem[] {
  if (type === 'all') return [...allMedia];
  return allMedia.filter((item) => item.type === (type as MediaType));
}

export function getHotMedia(limit = 12): MediaItem[] {
  return [...allMedia].sort((a, b) => b.popularity - a.popularity).slice(0, limit);
}

export function getLatestMedia(limit = 12): MediaItem[] {
  return [...allMedia]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, limit);
}

export interface MediaFilter {
  type?: string;
  genre?: string;
  region?: string;
  year?: string;
  sort?: string;
}

export function sortMedia(list: MediaItem[], sort = 'updated'): MediaItem[] {
  const sorted = [...list];
  if (sort === 'rating') {
    sorted.sort((a, b) => b.rating - a.rating);
  } else if (sort === 'popularity') {
    sorted.sort((a, b) => b.popularity - a.popularity);
  } else {
    sorted.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }
  return sorted;
}

export function filterMedia(filter: MediaFilter): MediaItem[] {
  let list = getByType(filter.type ?? 'all');
  if (filter.genre && filter.genre !== '全部') {
    list = list.filter((item) => item.genres.includes(filter.genre as string));
  }
  if (filter.region && filter.region !== '全部') {
    list = list.filter((item) => item.region === filter.region);
  }
  if (filter.year && filter.year !== '全部') {
    list = list.filter((item) => String(item.year) === filter.year);
  }
  return sortMedia(list, filter.sort);
}

export function searchMedia(keyword: string): MediaItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allMedia.filter((item) => {
    const haystack = [item.title, item.director, item.region, item.summary, ...item.cast, ...item.genres]
      .join(' ')
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getAllNews(): NewsItem[] {
  return [...allNews].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function getNewsById(id: string): NewsItem | undefined {
  return allNews.find((item) => item.id === id);
}

export function getRelatedNews(id: string, limit = 3): NewsItem[] {
  const current = getNewsById(id);
  if (!current) return getAllNews().slice(0, limit);
  const sameCategory = allNews.filter((item) => item.id !== id && item.category === current.category);
  const others = allNews.filter((item) => item.id !== id && item.category !== current.category);
  return [...sameCategory, ...others].slice(0, limit);
}

export function searchNews(keyword: string): NewsItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allNews.filter((item) => {
    const haystack = [item.title, item.summary, item.category, item.author, ...item.tags]
      .join(' ')
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRelatedMedia(id: string, limit = 6): MediaItem[] {
  const current = getMediaById(id);
  if (!current) return getHotMedia(limit);
  return allMedia
    .filter((item) => item.id !== id)
    .map((item) => {
      let score = 0;
      if (item.type === current.type) score += 2;
      if (item.region === current.region) score += 1;
      if (item.director === current.director) score += 3;
      score += item.genres.filter((g) => current.genres.includes(g)).length * 2;
      score += item.cast.filter((c) => current.cast.includes(c)).length * 3;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.popularity - a.item.popularity)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function formatPopularity(value: number): string {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}万`;
  return String(value);
}